import { useEffect, useRef } from 'react';
import { isTypingTarget, matchShortcut, type ShortcutScope, shortcutsIn } from './shortcut-map.js';

/** Handlers by shortcut id. An id with no handler is not bound. */
export type ShortcutHandlers = Readonly<Record<string, (() => void) | undefined>>;

export interface UseShortcutsOptions {
  /**
   * Fire even while somebody is typing.
   *
   * True only for the palette, whose `mod` shortcut has to work from inside a search field — that
   * is the point of having it. Everything in a working screen leaves the keyboard to the typist.
   */
  readonly whileTyping?: boolean;
}

/**
 * Binds one scope's shortcuts for as long as the component is mounted.
 *
 * The handlers are read through a ref rather than listed as dependencies: a caller passes a fresh
 * object every render, and a dependency on it would unbind and rebind the listener on each one,
 * which is both pointless and a way to miss a key press mid-render.
 *
 * An id with no handler is left unbound, so a shortcut never listens and does nothing — and
 * `preventDefault` happens only once an action is actually going to run, which keeps every other
 * combination the browser's.
 */
export function useShortcuts(
  scope: ShortcutScope,
  handlers: ShortcutHandlers,
  options: UseShortcutsOptions = {},
): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  const whileTyping = options.whileTyping ?? false;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!whileTyping && isTypingTarget(event.target)) {
        return;
      }
      const shortcut = matchShortcut(event, scope);
      if (shortcut === undefined) {
        return;
      }
      const handler = latest.current[shortcut.id];
      if (handler === undefined) {
        return;
      }
      event.preventDefault();
      handler();
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [scope, whileTyping]);
}

/** The ids of one scope, so a screen can see at a glance what it is expected to answer. */
export function shortcutIdsIn(scope: ShortcutScope): readonly string[] {
  return shortcutsIn(scope).map((shortcut) => shortcut.id);
}
